import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Check, X, RotateCcw, RefreshCw, Download, ListChecks,
  Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight,
  Loader2
} from 'lucide-react';
import { StatusPill } from '../../components/Table/Table';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import {
  fetchAdjustmentRequests,
  fetchOriginalAttendance,
  approveAdjustment,
  rejectAdjustment,
  setActiveTab,
  setDate,
  clearError,
  selectRequests,
  selectLoading,
  selectError,
  selectActiveTab,
  selectDepartment,
  selectDate,
  selectActionLoading,
} from '../../store/slices/attendanceApprovalSlice';

const STATUS_META = {
  Pending: { pill: 'bg-amber-50 text-amber-700', label: 'Pending' },
  Approved: { pill: 'bg-primary-50 text-primary-700', label: 'Approved' },
  Rejected: { pill: 'bg-red-50 text-red-700', label: 'Rejected' },
  Cancelled: { pill: 'bg-gray-50 text-gray-700', label: 'Cancelled' },
};

const TABS = ['Pending', 'Approved', 'Cancelled', 'All'];

const CODES_WITH_ORIGINAL = ['II', 'IO', 'IB', 'IIO'];

// ─── Format time with AM/PM
function formatTimeTo24h(timeStr) {
  if (!timeStr) return '';

  try {
    let hours = 0;
    let minutes = 0;

    if (/[AP]M/i.test(timeStr)) {
      const match = timeStr.match(/(\d{1,2})(?::(\d{2}))?/);
      if (!match) return timeStr;

      hours = parseInt(match[1], 10);
      minutes = match[2] !== undefined ? parseInt(match[2], 10) : 0;

      const isPM = /PM/i.test(timeStr);
      if (isPM && hours < 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;
    } else if (timeStr.includes('T') || (timeStr.includes('-') && timeStr.includes(':'))) {
      const date = new Date(timeStr);
      if (!isNaN(date.getTime())) {
        hours = date.getHours();
        minutes = date.getMinutes();
      } else {
        return timeStr;
      }
    } else if (timeStr.includes(':')) {
      const timePart = timeStr.includes('.') ? timeStr.split('.')[0] : timeStr;
      const parts = timePart.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parts[1] !== undefined ? parseInt(parts[1], 10) : 0;
    } else {
      return timeStr;
    }

    if (isNaN(hours) || isNaN(minutes)) return timeStr;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } catch {
    return timeStr;
  }
}

function formatDateToYYYYMMDD(dateStr) {
  if (!dateStr) return '—';

  try {
    let date;

    if (dateStr.includes('T')) {
      date = new Date(dateStr);
    } else if (dateStr.includes('-')) {
      return dateStr;
    } else if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        date = new Date(`${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`);
      }
    } else {
      date = new Date(dateStr);
    }

    if (!date || isNaN(date.getTime())) {
      return dateStr;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch {
    return dateStr;
  }
}

function stripInOutLabels(value) {
  if (!value) return value;
  return value
    .replace(/\b(IN|OUT)\b\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatDisplayTime(value) {
  if (!value || value === '—') return value || '';
  if (value.includes(' / ')) {
    return value;
  }
  const stripped = stripInOutLabels(value);
  if (!stripped) return '';
  return formatTimeTo24h(stripped);
}

function safeToString(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

// ─── Normalize date for comparison
function normalizeDateForComparison(dateStr) {
  if (!dateStr) return '';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    let d = new Date(dateStr);

    if (isNaN(d.getTime())) {
      const parts = dateStr.split(/[/-]/);
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          d = new Date(parts[0], parts[1] - 1, parts[2]);
        } else {
          // MM/DD/YYYY - assumed
          d = new Date(parts[2], parts[0] - 1, parts[1]);
        }
      }
    }

    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

function hasOriginalData(request) {
  if (!request) return false;
  const code = request.adjTypeCode;
  if (!code || !CODES_WITH_ORIGINAL.includes(code)) return false;
  const original = request.original;
  return Boolean(original) && original !== '—';
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────

export default function AttendanceApproval() {
  const dispatch = useDispatch();

  // Local state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Redux state
  const requests = useSelector(selectRequests);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const activeTab = useSelector(selectActiveTab);
  const department = useSelector(selectDepartment);
  const date = useSelector(selectDate);
  const actionLoading = useSelector(selectActionLoading);

  // Employees for the search dropdown
  const { employees: reduxEmployees, loading: employeesLoading } = useSelector(
    (s) => s.employees || { employees: [], loading: false }
  );

  const employees = useMemo(() => {
    if (!Array.isArray(reduxEmployees)) return [];
    return reduxEmployees
      .map((emp) => ({
        employeeId: String(emp.HED_EMPLOYEE_ID),
        serviceNo: emp.HED_SERVICE_NO || '',
        name:
          emp.HED_REPORT_NAME ||
          `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() ||
          `Employee ${emp.HED_SERVICE_NO}`,
        department: emp.HDD_DEPARTMENT_NAME || '',
      }))
      .filter((emp) => emp.employeeId);
  }, [reduxEmployees]);

  // Get current user ID
  const currentUserId = useMemo(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user?.id || user?.userId || user?.employeeId || null;
      } catch (e) {
        // fall through to other sources
      }
    }

    const userId =
      localStorage.getItem('userId') ||
      localStorage.getItem('EmployeeId') ||
      sessionStorage.getItem('userId');

    return userId ? Number(userId) : 1;
  }, []);

  // Reload requests and their original attendance
  const refreshData = async () => {
    try {
      const result = await dispatch(fetchAdjustmentRequests()).unwrap();
      if (result && result.length > 0) {
        await dispatch(fetchOriginalAttendance({ requests: result }));
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleManualRefresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      await refreshData();
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Load employees on mount
  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  // Handlers
  const handleApprove = async (id) => {
    try {
      await dispatch(approveAdjustment({
        adjustmentId: id,
        userId: currentUserId,
      })).unwrap();
      await refreshData();
    } catch (err) {
      console.error('Approval failed:', err);
    }
  };

  const handleReject = async (id) => {
    try {
      await dispatch(rejectAdjustment({
        adjustmentId: id,
        userId: currentUserId,
      })).unwrap();
      await refreshData();
    } catch (err) {
      console.error('Rejection failed:', err);
    }
  };

  const handleRevert = () => {
    refreshData();
  };

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
  };

  // Calculate counts
  const counts = useMemo(
    () => ({
      total: requests.length,
      pending: requests.filter((r) => r?.status === 'Pending').length,
      approved: requests.filter((r) => r?.status === 'Approved').length,
      cancelled: requests.filter((r) => r?.status === 'Cancelled').length,
      rejected: requests.filter((r) => r?.status === 'Rejected').length,
    }),
    [requests]
  );

  // Check if any request has original data to display
  const hasAnyOriginal = useMemo(() => {
    return requests.some((r) => hasOriginalData(r));
  }, [requests]);

  // ─── Filter requests
  const filtered = useMemo(() => {
    return requests.filter((r) => {
      if (!r) return false;

      // Tab filter
      if (activeTab !== 'All' && r.status !== activeTab) return false;

      // Department filter
      if (department !== 'All departments' && r.department !== department) return false;

      // Date filter
      if (date) {
        const filterDate = normalizeDateForComparison(date);
        const requestDate = r.attendanceDate || normalizeDateForComparison(r.date);

        if (filterDate && requestDate && requestDate !== filterDate) {
          return false;
        }
      }

      // Employee filter (null or "All Employees" = no filter)
      if (selectedEmployee && !selectedEmployee.isAll) {
        if (safeToString(r.staffId) !== safeToString(selectedEmployee.serviceNo)) {
          return false;
        }
      }

      return true;
    });
  }, [requests, activeTab, department, date, selectedEmployee]);


  const changeTab = (tab) => {
    dispatch(setActiveTab(tab));
  };

  const handleDateChange = (e) => {
    dispatch(setDate(e.target.value));
  };


  const handleExport = () => {
    const data = filtered.map((r) => ({
      'Employee': safeToString(r.name),
      'Staff ID': safeToString(r.staffId),
      'Department': safeToString(r.department),
      'Date': safeToString(r.date),
      'Type': safeToString(r.type),
      'Original': hasOriginalData(r) ? formatDisplayTime(r.original) : 'N/A',
      'Requested': formatDisplayTime(r.requested),
      'Reason': safeToString(r.reason),
      'Status': safeToString(r.status),
    }));

    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map((row) => headers.map((h) => `"${row[h] || ''}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-adjustments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-full bg-surface-50">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Approval</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review and act on attendance adjustment requests submitted by employees.
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-surface-50"
        >
          <Download size={15} />
          Export
        </button>
      </div>

      {error && (
        <div className="mb-6 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-5 py-3">
          <p className="text-sm font-medium text-red-700">❌ {error}</p>
          <button
            onClick={() => dispatch(clearError())}
            className="text-red-700 hover:text-red-900"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<ListChecks size={18} className="text-gray-500" />}
          iconBg="bg-gray-100"
          value={counts.total}
          label="Total requests"
          loading={loading}
        />
        <StatCard
          icon={<Clock size={18} className="text-amber-500" />}
          iconBg="bg-amber-50"
          value={counts.pending}
          label="Pending review"
          loading={loading}
        />
        <StatCard
          icon={<CheckCircle2 size={18} className="text-primary-600" />}
          iconBg="bg-primary-50"
          value={counts.approved}
          label="Approved"
          loading={loading}
        />
        <StatCard
          icon={<XCircle size={18} className="text-red-500" />}
          iconBg="bg-red-50"
          value={counts.cancelled}
          label="Cancelled"
          loading={loading}
        />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Title strip */}
        {/* Title strip */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-gray-900">Adjustment Requests</h3>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={refreshing || loading}
              title="Refresh requests"
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <span className="text-xs text-gray-500">
              {filtered.length === 1 ? '1 record' : `${filtered.length} records`}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
            {/* Employee search */}
            <div className="flex-1 sm:max-w-xs">
              <EmployeeSearch
                value={selectedEmployee}
                onSelect={handleEmployeeSelect}
                employees={employees}
                loading={employeesLoading}
                showAllOption={false}
              />
            </div>

            {/* Date filter */}
            <input
              type="date"
              value={date}
              onChange={handleDateChange}
              className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-xs text-gray-700 outline-none focus:border-primary-500"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 rounded-lg bg-gray-50 p-1">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => changeTab(tab)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${activeTab === tab
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {tab}
                {tab !== 'All' && (
                  <span className="ml-1.5 text-xs text-gray-400">
                    ({counts[tab.toLowerCase()] || 0})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-gray-400">
            <Loader2 size={15} className="animate-spin" /> Loading...
          </div>
        ) : (
          <div className="overflow-x-clip">
            <table className="w-full min-w-[950px] border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <Th>Employee</Th>
                  <Th>Service No</Th>
                  <Th>Department</Th>
                  <Th>Date</Th>
                  <Th>Type</Th>
                  {hasAnyOriginal && <Th>Original</Th>}
                  <Th>Requested</Th>
                  <Th>Reason</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={hasAnyOriginal ? 10 : 9}
                      className="py-20 text-center text-sm text-gray-400"
                    >
                      {requests.length === 0
                        ? 'No adjustment requests found.'
                        : 'No requests match your filters.'}
                    </td>
                  </tr>
                )}
                {filtered.map((r, index) => {
                  const showOriginal = hasOriginalData(r);
                  const formattedOriginal = showOriginal ? formatDisplayTime(r.original) : '';
                  const formattedRequested = formatDisplayTime(r.requested);
                  const isPending = r?.status === 'Pending';
                  const busy = actionLoading === r?.id;

                  return (
                    <tr
                      key={r?.id || `row-${index}`}
                      className="border-b border-gray-100 transition-colors last:border-0 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{safeToString(r?.name) || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{safeToString(r?.staffId) || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{safeToString(r?.department) || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDateToYYYYMMDD(r?.date)}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{safeToString(r?.type) || '—'}</td>
                      {hasAnyOriginal && (
                        <td className="px-4 py-3 font-medium text-gray-800">{formattedOriginal}</td>
                      )}
                      <td className="px-4 py-3 font-medium text-primary-700">{formattedRequested}</td>
                      <td
                        className="max-w-[250px] px-4 py-3 text-gray-600"
                        title={safeToString(r?.reason)}
                      >
                        <div className="whitespace-normal break-words">
                          {safeToString(r?.reason) || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill
                          label={STATUS_META[r?.status]?.label || safeToString(r?.status) || '—'}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {r?.status === 'Approved' ? (
                          <span className="text-xs text-gray-400" title="Approved requests can't be changed">
                            —
                          </span>
                        ) : r?.status === 'Cancelled' ? (
                          <div className="flex items-center gap-2">
                            <ActionButton
                              title="Approve"
                              onClick={() => handleApprove(r.id)}
                              className="border-primary-200 text-primary-600 hover:bg-primary-50"
                              disabled={busy}
                            >
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            </ActionButton>
                            <ActionButton
                              title="Revert to pending"
                              onClick={() => handleRevert(r.id)}
                              className="border-gray-200 text-gray-500 hover:bg-gray-50"
                              disabled={busy}
                            >
                              <RotateCcw size={14} />
                            </ActionButton>
                          </div>
                        ) : isPending ? (
                          <div className="flex items-center gap-2">
                            <ActionButton
                              title="Approve"
                              onClick={() => handleApprove(r.id)}
                              className="border-primary-200 text-primary-600 hover:bg-primary-50"
                              disabled={busy}
                            >
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            </ActionButton>
                            <ActionButton
                              title="Reject"
                              onClick={() => handleReject(r.id)}
                              className="border-red-200 text-red-500 hover:bg-red-50"
                              disabled={busy}
                            >
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                            </ActionButton>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <ActionButton
                              title="Approve"
                              onClick={() => handleApprove(r.id)}
                              className="border-primary-200 text-primary-600 hover:bg-primary-50"
                              disabled={busy}
                            >
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            </ActionButton>
                            <ActionButton
                              title="Reject"
                              onClick={() => handleReject(r.id)}
                              className="border-red-200 text-red-500 hover:bg-red-50"
                              disabled={busy}
                            >
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                            </ActionButton>
                            <ActionButton
                              title="Revert to pending"
                              onClick={() => handleRevert(r.id)}
                              className="border-gray-200 text-gray-500 hover:bg-gray-50"
                              disabled={busy}
                            >
                              <RotateCcw size={14} />
                            </ActionButton>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── HELPER COMPONENTS (module level) ────────────────────────────────────

function Th({ children }) {
  return (
    <th className="sticky -top-6 z-30 whitespace-nowrap bg-gray-50 px-4 py-3 text-left text-[12px] font-semibold text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">
      {children}
    </th>
  );
}

function StatCard({ icon, iconBg, value, label, loading }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
        {loading ? <Loader2 size={18} className="animate-spin text-gray-400" /> : icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{loading ? '...' : value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function ActionButton({ children, className = '', disabled = false, ...props }) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={`flex h-7 w-7 items-center justify-center rounded-md border bg-white transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'
        } ${className}`}
    >
      {children}
    </button>
  );
}